/**
 * Cognito Authentication Provider
 * Handles AWS Cognito login, token management, and user session
 */

import React, { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react'
import { getRuntimeConfig } from '../config/runtime'
import { authenticateUser, AuthTokens, ChallengeRequired, clearTokens, getStoredTokens, LoginCredentials, NewPasswordChallengeInput, respondToNewPasswordChallenge, storeTokens } from '../services/authService'


export interface CognitoUser {
  email: string
  name: string
  id?: string
  c1OrgId?: string
  organisationName?: string
  entityType?: string
}

export interface CognitoContextType {
  user: CognitoUser | null
  tokens: AuthTokens | null
  isLoading: boolean
  error: string | null
  login: (credentials: LoginCredentials) => Promise<AuthTokens | ChallengeRequired>
  /**
   * Complete a NEW_PASSWORD_REQUIRED challenge and commit the session (AL-470).
   * Owned by the provider so that first login ends in the same authenticated
   * state as an ordinary login.
   */
  completeNewPassword: (input: NewPasswordChallengeInput) => Promise<AuthTokens>
  logout: () => void
  isAuthenticated: boolean
  updateUserFromAuthResponse: (authResponse: any) => void
}

const CognitoContext = createContext<CognitoContextType | undefined>(undefined)

/** Build a user from an ID token, falling back to a username when undecodable. */
function userFromIdToken(idToken: string, fallbackEmail = ''): CognitoUser {
  try {
    const decoded = JSON.parse(atob(idToken.split('.')[1]))
    const fullName = decoded.name
      || (decoded.given_name && decoded.family_name ? `${decoded.given_name} ${decoded.family_name}` : '')
      || decoded.given_name
      || ''
    return {
      email: decoded.email || decoded['cognito:username'] || fallbackEmail,
      name: fullName,
      id: decoded.sub,
    }
  } catch {
    return { email: fallbackEmail, name: 'User' }
  }
}

export const CognitoProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<CognitoUser | null>(null)
  const [tokens, setTokens] = useState<AuthTokens | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Initialize auth state from stored tokens on app load (on page refresh)
  useEffect(() => {
    const initializeAuthState = () => {
      const storedTokens = getStoredTokens()
      
      if (storedTokens) {
        setTokens(storedTokens)
        
        // Try to extract user info from stored JWT
        try {
          const decoded = JSON.parse(atob(storedTokens.idToken.split('.')[1]))
          const fullName = decoded.name
            || (decoded.given_name && decoded.family_name ? `${decoded.given_name} ${decoded.family_name}` : '')
            || decoded.given_name
            || ''
          setUser({
            email: decoded.email || decoded['cognito:username'] || '',
            name: fullName,
            id: decoded.sub,
          })
        } catch (e) {
          setUser({
            email: 'user@example.com',
            name: 'User',
          })
        }
      } else {
      }
      
      setIsLoading(false)
    }
    
    initializeAuthState()
  }, [])

  // Update user name from WebSocket auth response
  const updateUserFromAuthResponse = useCallback((authResponse: any) => {
    // Log the full auth response to see what's available
    console.log('[CognitoProvider] Auth response received:', JSON.stringify(authResponse, null, 2));
    
    // Extract name from WebSocket AuthConfirmation response
    const wsName = authResponse?.AuthConfirmation?.nameLabelDisplay;
    const wsOrgId = authResponse?.AuthConfirmation?.orgLabelDisplay ?? authResponse?.AuthConfirmation?.organizationId ?? authResponse?.AuthConfirmation?.orgIdentifier;
    const wsOrgName = authResponse?.AuthConfirmation?.organisationName ?? authResponse?.AuthConfirmation?.orgLabelDisplay;
    const wsEntityType = authResponse?.AuthConfirmation?.entityType ?? authResponse?.AuthConfirmation?.c1EntityType;

    console.log('[CognitoProvider] Extracted values:', { wsName, wsOrgId, wsOrgName, wsEntityType });

    if (wsName || wsOrgId || wsOrgName || wsEntityType) {
      setUser((prevUser) => {
        if (prevUser) {
          const updatedUser = {
            ...prevUser,
            ...(wsName ? { name: wsName } : {}),
            ...(wsOrgId ? { c1OrgId: wsOrgId } : {}),
            ...(wsOrgName ? { organisationName: wsOrgName } : {}),
            ...(wsEntityType ? { entityType: wsEntityType } : {}),
          };
          console.log('[CognitoProvider] Updated user:', updatedUser);
          return updatedUser;
        }
        return prevUser
      })
    } else {
    }
  }, [])

  const login = useCallback(async (credentials: LoginCredentials): Promise<AuthTokens | ChallengeRequired> => {
    setIsLoading(true)
    setError(null)
    try {
      const config = getRuntimeConfig()
      
      
      // Use real Cognito authentication
      const result = await authenticateUser(credentials)
      

      // Check if it's a challenge (password reset, etc)
      if ('challengeName' in result) {
        return result
      }
      
      // Extract user info from JWT if available
      try {
        const decoded = JSON.parse(atob(result.idToken.split('.')[1]))
        const fullName = decoded.name
          || (decoded.given_name && decoded.family_name ? `${decoded.given_name} ${decoded.family_name}` : '')
          || decoded.given_name
          || ''
        setUser({
          email: decoded.email || decoded['cognito:username'] || credentials.username,
          name: fullName,
          id: decoded.sub,
        })
      } catch (e) {
        setUser({
          email: credentials.username,
          name: 'User',
        })
      }
      
      setTokens(result)
      
      // Store tokens in cookies for session persistence across page refreshes
      storeTokens(result)
      
      return result
    } catch (err: any) {
      // Clear tokens on failed login
      clearTokens()
      setUser(null)
      setTokens(null)
      // AL-470: deliberately not stored on the context. It used to hold the raw
      // Cognito message, which the login screen then displayed — overwriting
      // the mapped copy and leaking whether an account exists. Callers map the
      // thrown AuthError's code to their own wording instead.
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [])

  /**
   * AL-470: complete the first-login password challenge and commit the session.
   *
   * Previously the Auth component called respondToNewPasswordChallenge and
   * storeTokens itself, then navigated. Storage was written but this provider's
   * in-memory `tokens` was not, so `isAuthenticated` stayed false and
   * ProtectedRoute redirected the user straight back to /login — appearing as
   * though the new password had not saved. Only a page reload recovered it,
   * because that remounts the provider and re-reads storage.
   *
   * Committing here means first login ends in exactly the same state as an
   * ordinary login, and navigation follows from `isAuthenticated` as usual.
   */
  const completeNewPassword = useCallback(async (input: NewPasswordChallengeInput): Promise<AuthTokens> => {
    setIsLoading(true)
    setError(null)
    try {
      const newTokens = await respondToNewPasswordChallenge(input)

      storeTokens(newTokens)
      setUser(userFromIdToken(newTokens.idToken, input.username))
      setTokens(newTokens)

      return newTokens
    } catch (err: any) {
      clearTokens()
      setUser(null)
      setTokens(null)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [])

  const logout = useCallback(() => {
    clearTokens()
    setUser(null)
    setTokens(null)
    setError(null)
  }, [])

  return (
    <CognitoContext.Provider
      value={{
        user,
        tokens,
        isLoading,
        error,
        login,
        completeNewPassword,
        logout,
        isAuthenticated: !!tokens?.idToken,
        updateUserFromAuthResponse,
      }}
    >
      {children}
    </CognitoContext.Provider>
  )
}

export const useCognito = () => {
  const context = useContext(CognitoContext)
  if (!context) {
    throw new Error('useCognito must be used within CognitoProvider')
  }
  return context
}
